import { supabase } from './supabase';

// ============================================================================
// Cross-app token resolution
// ----------------------------------------------------------------------------
// When the OR app is opened from Forge/Surgery/Revela/Mind, the URL contains
// a ?token=xxx parameter. This token is validated server-side and resolves
// to the org/patient/encounter/provider context that the source app set up.
//
// Tokens are valid for 4 hours and are single-use (consumed_at gets set when
// validated). The OR app stores the resolved context in sessionStorage so a
// page refresh doesn't re-consume the token.
// ============================================================================

export interface EncounterContext {
    org_id: string;
    patient_id: number | null;
    encounter_id: number | null;
    provider_id: number | null;
    facility_id: number | null;
    source_app: string | null;
    via_token: boolean;
}

const SESSION_KEY = 'pt-or:encounter-context';

export async function resolveContext(): Promise<EncounterContext | null> {
    // 1) Check session storage first (avoids re-consuming a one-time token on refresh)
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
        try {
            return JSON.parse(stored) as EncounterContext;
        } catch {
            sessionStorage.removeItem(SESSION_KEY);
        }
    }

    // 2) Look for ?token= in URL
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (token) {
        const { data, error } = await supabase.rpc('validate_cross_app_token', {
            p_token: token
        });

        if (error) {
            // eslint-disable-next-line no-console
            console.error('[PT-OR] Cross-app token validation failed:', error);
            return null;
        }

        if (data?.valid) {
            const ctx: EncounterContext = {
                org_id: data.org_id,
                patient_id: data.patient_id ?? null,
                encounter_id: data.encounter_id ?? null,
                provider_id: data.provider_id ?? null,
                facility_id: data.facility_id ?? null,
                source_app: data.source_app ?? null,
                via_token: true
            };
            sessionStorage.setItem(SESSION_KEY, JSON.stringify(ctx));

            // Clean the token off the URL so refresh doesn't try to re-validate
            const url = new URL(window.location.href);
            url.searchParams.delete('token');
            window.history.replaceState({}, '', url.toString());

            return ctx;
        }
    }

    // 3) No token — return null so caller can fall back to org_id from session
    return null;
}

export function clearContext(): void {
    sessionStorage.removeItem(SESSION_KEY);
}

export async function getCurrentOrgId(): Promise<string | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    // 2026-08-05 fix: .maybeSingle() alone does NOT protect against an
    // account active in more than one org — PostgREST forces a
    // single-object response format for BOTH .single() and .maybeSingle()
    // (the only difference is 0-row handling), so 2+ matching rows error
    // out either way. Confirmed live elsewhere on this estate: an account
    // holds active membership in two orgs, which would make this return
    // null for that account, cascading to useOrgContext throwing "No
    // organization context available" in production. .order + .limit(1)
    // deterministically resolves to the most recently created active
    // membership before the single-row constraint applies, instead of
    // erroring on it.
    const { data, error } = await supabase
        .from('org_members')  // Note: requires public view forwarding to saas.org_members
        .select('org_id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

    if (error) {
        // eslint-disable-next-line no-console
        console.warn('[PT-OR] org_members lookup error (likely missing public view):', error);
        return null;
    }

    return data?.org_id ?? null;
}
